import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';
import { useNavigate } from 'react-router-dom';

const notifyChanged = () => window.dispatchEvent(new Event('credits:changed'));
const FONT = { fontFamily: '"Times New Roman", Times, serif' };

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

function Modal({ children, onClose, wide }) {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className={`w-full ${wide ? 'max-w-2xl' : 'max-w-md'} rounded-2xl bg-white p-6 shadow-2xl border border-[#F3E8FF]`}
        style={FONT}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

export function BuyCreditsModal({ onClose, message }) {
  const navigate = useNavigate();

  const goToTopUps = () => {
    if (onClose) onClose();
    navigate('/employer/settings/billing?tab=credits');
  };

  return (
    <Modal onClose={onClose}>
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-xl font-extrabold text-[#3B0764]">💳 Buy Candidate Credits</h3>
          <p className="text-sm text-gray-500 mt-0.5">Top up your candidate-unlock credits to view contact details beyond your plan's limit</p>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          aria-label="Close"
        >
          &times;
        </button>
      </div>

      {message && <p className="mt-3 text-sm text-[#9333EA] bg-[#FDF4FF] border border-[#F3E8FF] rounded-lg px-3 py-2">{message}</p>}

      <p className="mt-6 text-sm text-gray-600">
        Choose a candidate credit top-up pack to unlock more candidate contact details.
      </p>

      <div className="mt-5 flex items-center gap-3">
        <button
          type="button"
          onClick={goToTopUps}
          className="flex-1 bg-gradient-to-r from-[#9333EA] to-[#EC4899] text-white font-semibold px-6 py-3 rounded-xl hover:shadow-lg transition"
        >
          View Top-Up Packs
        </button>
        <button onClick={onClose} className="text-sm text-gray-500 hover:underline">
          Cancel
        </button>
      </div>
    </Modal>
  );
}
export default function UnlockContact({ candidateId, onUnlocked }) {
  const [step, setStep] = useState(null); // null | 'confirm' | 'buy'
  const [info, setInfo] = useState(null);
  const [checking, setChecking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const start = async () => {
    setError('');
    setChecking(true);
    try {
      const { data } = await api.get('/employer/credits/balance');
      setInfo(data);
      if (data.quota_remaining === null || data.quota_remaining > 0) {
        doUnlock();
      } else if (data.balance >= data.credits_per_unlock) {
        setStep('confirm');
      } else {
        setStep('buy');
      }
    } catch (e) {
      setError('Could not check your credits. Try again.');
    } finally {
      setChecking(false);
    }
  };

  const doUnlock = async () => {
    setBusy(true);
    setError('');
    try {
      const { data } = await api.post(`/employer/candidates/${candidateId}/unlock`);
      notifyChanged();
      setStep(null);
      onUnlocked({ phone: data.phone, email: data.email, resume_path: data.resume_path, resume_url: data.resume_url });
    } catch (e) {
      const d = e.response?.data;
      if (d?.upgrade_required) {
        setInfo((i) => ({
          ...(i || {}),
          balance: d.credit_balance,
          credits_per_unlock: d.credits_per_unlock,
          plan_name: d.plan_name,
          candidate_unlock_limit: d.candidate_unlock_limit,
        }));
        setStep('buy');
      } else if (e.response?.status === 402) {
        setInfo((i) => ({ ...(i || {}), balance: e.response.data.balance, credits_per_unlock: e.response.data.required }));
        setStep('buy');
      } else {
        setError(e.response?.data?.message || 'Could not unlock. Try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={start}
        disabled={checking}
        style={FONT}
        className="inline-flex items-center gap-1.5 bg-gradient-to-r from-[#9333EA] to-[#EC4899] text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-60 hover:shadow-md transition"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
          <rect x="4" y="11" width="16" height="10" rx="2" />
          <path d="M8 11V7a4 4 0 018 0v4" />
        </svg>
        {checking ? 'Checking…' : 'Unlock phone & email'}
      </button>
      {error && step === null && <span className="text-xs text-red-600">{error}</span>}

      {step === 'confirm' && info && (
        <Modal onClose={busy ? undefined : () => setStep(null)}>
          <h3 className="text-lg font-extrabold text-[#3B0764]">Unlock contact details?</h3>
          <p className="mt-2 text-sm text-gray-600">
            This reveals the phone number, email and resume for {info.credits_per_unlock} credits. Your balance is {info.balance}.
            Once unlocked, it stays unlocked for your company.
          </p>
          {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
          <div className="mt-5 flex gap-2">
            <button onClick={doUnlock} disabled={busy} className="rounded-lg bg-gradient-to-r from-[#9333EA] to-[#EC4899] px-4 py-2 text-sm font-semibold text-white hover:shadow-md disabled:opacity-60">
              {busy ? 'Unlocking…' : `Unlock for ${info.credits_per_unlock} credits`}
            </button>
            <button onClick={() => setStep(null)} disabled={busy} className="rounded-lg border border-[#F3E8FF] px-4 py-2 text-sm font-semibold text-gray-700">
              Cancel
            </button>
          </div>
        </Modal>
      )}

      {step === 'buy' && (
        <BuyCreditsModal
          message={info ? (
            info.plan_name
              ? `You've used all ${info.candidate_unlock_limit} candidate unlock${info.candidate_unlock_limit === 1 ? '' : 's'} on your ${info.plan_name} plan. Unlocking needs ${info.credits_per_unlock} credits — your balance is ${info.balance}.`
              : `Unlocking needs ${info.credits_per_unlock} credits. Your balance is ${info.balance}.`
          ) : ''}
          onClose={() => setStep(null)}
          onPurchased={() => doUnlock()}
        />
      )}
    </>
  );
}

export function CreditsHeader() {
  const [balance, setBalance] = useState(null);
  const navigate = useNavigate();

  const refresh = useCallback(() => {
    api.get('/employer/credits/balance').then((r) => setBalance(r.data.balance)).catch(() => {});
  }, []);

  useEffect(() => {
    refresh();
    window.addEventListener('credits:changed', refresh);
    return () => window.removeEventListener('credits:changed', refresh);
  }, [refresh]);

  return (
    <button
      type="button"
      onClick={() => navigate('/employer/settings/billing?tab=credits')}
      style={FONT}
      className="inline-flex items-center gap-2 rounded-lg border-2 border-[#9333EA] px-3 py-1.5 text-sm font-semibold text-[#9333EA] hover:bg-[#FDF4FF]"
    >
      <span>{balance === null ? '…' : balance} credits</span>
      <span className="hidden sm:inline">· Buy Credits</span>
    </button>
  );
}
